import type { Href } from 'expo-router';
import { ChevronDownIcon } from 'lucide-react-native';
import * as React from 'react';
import { useState } from 'react';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Accordion, ScrollView, Square, XStack, YStack } from 'tamagui';

import { isNonMobileWeb } from '../../constants';
import { useThemeColors } from '../../themes/SmoothieColorTokens';
import { BackButton, BackButtonIconType } from '../buttons/BackButton';
import { Button, ButtonSize, ButtonVariant } from '../buttons/Button';
import { Icon } from '../icons/Icon';
import {
    BodyLargeEmphasized,
    BodyLargeRegular,
    BodyMediumRegular,
    H2Emphasized,
} from '../text/Typography';

interface FAQ {
    question: string;
    answer: string | React.ReactNode;
}

interface FAQHeaderProps {
    title: string;
    description?: string;
    fallbackRoute: Href;
}

interface FAQFooterProps {
    footerComponent?: React.JSX.Element;
    footerInfo?: string;
    onButtonPress?: () => void;
}

interface FAQAccordionScreenProps extends FAQHeaderProps, FAQFooterProps {
    faqs: FAQ[];
    defaultOpenQuestions?: string[];
}

const FAQHeader = ({ title, description, fallbackRoute }: FAQHeaderProps) => {
    return (
        <YStack marginHorizontal={16} marginBottom={24}>
            <XStack
                justifyContent="flex-end"
                $sm={{
                    // Special positioning for the button on larger screens because the page layout is different
                    position: 'absolute',
                    top: -45,
                    right: -20,
                }}
            >
                <BackButton
                    fallbackRoute={fallbackRoute}
                    iconType={BackButtonIconType.X}
                />
            </XStack>
            <H2Emphasized color="$peach1200" marginTop={24}>
                {title}
            </H2Emphasized>
            <BodyLargeRegular color="$peach1100" marginTop={8}>
                {description}
            </BodyLargeRegular>
        </YStack>
    );
};

const AccordionItem = ({
    question,
    answer,
    rotate,
    showAnswer,
}: {
    question: string;
    answer: string | React.ReactNode;
    rotate: string;
    showAnswer: boolean;
}) => {
    return (
        <Accordion.Item
            value={question}
            borderBottomWidth={1}
            borderColor="$gray200"
        >
            <Accordion.Trigger borderTopWidth={0} borderBottomWidth={0}>
                <XStack alignItems="center">
                    <BodyLargeEmphasized>{question}</BodyLargeEmphasized>
                    <Square
                        animation="tooltip"
                        rotate={rotate}
                        marginLeft="auto"
                    >
                        <Icon icon={ChevronDownIcon} color="$gray1100" />
                    </Square>
                </XStack>
            </Accordion.Trigger>
            <Accordion.Content paddingTop={4}>
                {showAnswer &&
                    (typeof answer === 'string' ? (
                        <BodyMediumRegular
                            color="$gray1100"
                            whiteSpace="pre-wrap"
                        >
                            {answer}
                        </BodyMediumRegular>
                    ) : (
                        answer
                    ))}
            </Accordion.Content>
        </Accordion.Item>
    );
};

const FAQFooter = ({
    footerComponent,
    footerInfo,
    onButtonPress,
}: FAQFooterProps) => {
    return (
        <YStack
            backgroundColor="$white"
            paddingHorizontal={16}
            paddingBottom={16}
            gap={16}
        >
            {footerComponent}
            <BodyMediumRegular>{footerInfo}</BodyMediumRegular>
            <Button
                onPress={onButtonPress}
                variant={ButtonVariant.SECONDARY}
                size={ButtonSize.LARGE}
                text="Contact Fruitful Hospitality"
                width="auto"
            />
        </YStack>
    );
};

export const FAQAccordionScreen = ({
    title,
    description,
    faqs,
    footerComponent,
    footerInfo,
    fallbackRoute,
    onButtonPress,
    defaultOpenQuestions = [],
}: FAQAccordionScreenProps) => {
    const { getColor } = useThemeColors();
    const [openItems, setOpenItems] = useState<string[]>(defaultOpenQuestions);

    return (
        <SafeAreaView
            style={{
                flex: 1,
                backgroundColor: getColor('$peach300'),
                ...(isNonMobileWeb && {
                    marginTop: 70,
                    borderRadius: 16,
                    width: 700,
                    alignSelf: 'center',
                }),
            }}
            edges={['top']}
        >
            <FAQHeader
                title={title}
                description={description}
                fallbackRoute={fallbackRoute}
            />
            <ScrollView backgroundColor="$white">
                <Accordion
                    type="multiple"
                    value={openItems}
                    paddingBottom={24}
                    onValueChange={(val) => {
                        setOpenItems(val);
                    }}
                >
                    {faqs.map((faq: FAQ) => {
                        const { question, answer } = faq;
                        return (
                            <AccordionItem
                                key={question}
                                showAnswer={openItems.includes(question)}
                                question={question}
                                answer={answer}
                                rotate={
                                    openItems.includes(question)
                                        ? '180deg'
                                        : '0deg'
                                }
                            />
                        );
                    })}
                </Accordion>
                {(footerInfo != null || onButtonPress != null) && (
                    <FAQFooter
                        footerComponent={footerComponent}
                        footerInfo={footerInfo}
                        onButtonPress={onButtonPress}
                    />
                )}
            </ScrollView>
        </SafeAreaView>
    );
};
